import { Component, OnInit, Inject, Injector } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { finalize, timeout } from 'rxjs';
import { BaseComponent } from 'src/app/common/base/base.component';
import { ValueType } from 'src/app/common/enumerations/enumerations';
import { RegisterComponent } from 'src/app/common/extensions/decorator';
import { MessagePlaceholder } from 'src/app/common/models/placeholder';
import { SiteConfiguration } from 'src/app/components/site-configuration/site-configuration';
import { SiteConfigurationService } from 'src/app/components/site-configuration/site-configuration.service';

interface SiteConfigurationGroup {
  title: string;
  items: SiteConfiguration[];
}

@RegisterComponent('SiteConfigurationComponent')
@Component({
  selector: 'app-site-configuration',
  templateUrl: './site-configuration.component.html',
  styleUrls: ['./site-configuration.component.scss']
})
export class SiteConfigurationComponent extends BaseComponent implements OnInit {

  static key = 'SiteConfigurationComponent';
  loading = false;
  siteConfiguration: SiteConfiguration[] = [];
  configGroups: SiteConfigurationGroup[] = [];
  valueType = ValueType;

  constructor(
    protected override injector: Injector,
    public siteConfService: SiteConfigurationService,
    public dialogRef: MatDialogRef<SiteConfigurationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    super(injector);
  }

  ngOnInit() {
    this.loading = true;
    this.siteConfService.getAll().pipe(
      finalize(() => this.loading = false)
    ).subscribe({
      next: response => {
        this.siteConfiguration = (response || []).map(e => this.normalizeConfig(e));
        this.configGroups = this.buildConfigGroups(this.siteConfiguration);
      },
      error: () => {
        this.alert.defaultError();
      }
    });
  }

  onNoClick(submit?: boolean): void {
    this.dialogRef.close(submit);
  }


  async onSubmit() {
    this.loading = true;
    const saveConfigs = this.siteConfiguration.map(c => ({
      id: c.id,
      value: c.value == null ? '' : String(c.value)
    }));
    this.siteConfService.updateAll(saveConfigs).pipe(
      timeout(15000),
      finalize(() => this.loading = false)
    ).subscribe({
      next: () => {
        this.alert.message('updates', [new MessagePlaceholder('{what}', 'Site Configuration')]);
        this.dialogRef.close(true);
      },
      error: err => {
        console.error('updateAll failed', err);
        this.alert.defaultError();
      }
    });
  }

  getValueType(element: SiteConfiguration): ValueType | null {
    const valueType = element.valueType ?? element.type;
    return typeof valueType === 'number' ? valueType as ValueType : null;
  }

  trimTextValue(value: unknown): string {
    return String(value || '').trim();
  }

  hasInvalidConfiguration(): boolean {
    return this.siteConfiguration.some(config =>
      this.getValueType(config) === ValueType.Email && this.emailInvalid(config.value)
    );
  }

  emailInvalid(v: unknown): boolean {
    if (typeof v !== 'string') return true;
    // Light validation: lets most real emails through, blocks obvious bad ones
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    return !re.test(v);
  }

  emailTest() {
    this.siteConfService.emailTest().subscribe({
      error: () => this.alert.defaultError()
    });
  }

  private normalizeConfig(config: SiteConfiguration): SiteConfiguration {
    const valueType = this.getValueType(config);

    return {
      ...config,
      value: valueType === ValueType.Boolean
        ? ['true', '1', 'yes', 'on'].includes(String(config.value || '').trim().toLowerCase())
        : config.value
    };
  }

  private buildConfigGroups(configs: SiteConfiguration[]): SiteConfigurationGroup[] {
    const groupMap = new Map<string, SiteConfiguration[]>();
    const groupOrder = ['Notifications', 'Default Email Recipients', 'Links', 'General'];

    groupOrder.forEach(group => groupMap.set(group, []));
    configs.forEach(config => groupMap.get(this.getConfigGroupName(config))?.push(config));

    return groupOrder
      .map(title => ({ title, items: groupMap.get(title) || [] }))
      .filter(group => group.items.length);
  }

  private getConfigGroupName(config: SiteConfiguration): string {
    const name = (config.name || '').toLowerCase();
    const valueType = this.getValueType(config);

    if (valueType === ValueType.Boolean) {
      return 'Notifications';
    }

    if (name.includes('url') || name.includes('form')) {
      return 'Links';
    }

    if (valueType === ValueType.Email || name.includes('email')) {
      return 'Default Email Recipients';
    }

    return 'General';
  }

}
